import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Layout";
import Footer from "@/components/Footer";
import DemoButton from "@/components/DemoButton";
import { ShieldCheck, Lightning, ChartLineUp, LockKey, ArrowRight } from "@phosphor-icons/react";

const FEATURES = [
  { icon: LockKey, title: "Structurally anonymous", body: "Ballots carry no voter identity. Anonymity is enforced by the data model — not a toggle you can forget to flip." },
  { icon: Lightning, title: "One-time secure links", body: "Every voter gets a cryptographic single-use token. Double-voting is blocked atomically under load." },
  { icon: ChartLineUp, title: "Live tallies & Borda", body: "Single-choice percentages or ranked Borda points, turnout tracking, bar charts and CSV export." },
];

export default function Landing() {
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen grain relative">
      <header className="border-b border-zinc-200 bg-white/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <Link data-testid="nav-how" to="/how-it-works" className="hidden sm:inline px-3 py-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors">How it works</Link>
            <Link data-testid="nav-guide" to="/guide" className="hidden sm:inline px-3 py-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors">Guide</Link>
            <Link data-testid="nav-security" to="/security" className="hidden sm:inline px-3 py-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors">Security</Link>
            <Link data-testid="nav-pricing" to="/pricing" className="hidden sm:inline px-3 py-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors">Pricing</Link>
            <Link data-testid="landing-login" to="/auth" className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors">Sign in</Link>
            <Link data-testid="landing-cta" to="/auth?mode=register" className="px-5 py-2 text-sm font-semibold bg-black text-white rounded-full hover:bg-zinc-800 transition-colors active:scale-95">Start free</Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-24 pb-20 relative z-10">
        <div className="max-w-3xl vf-fade-up">
          <div className="inline-flex items-center gap-2 border border-zinc-300 rounded-full px-3 py-1 text-xs font-mono text-zinc-500 mb-8">
            <ShieldCheck size={14} weight="fill" className="text-[#1F6FE5]" /> SECURE ELECTIONS · MULTI-TENANT SAAS
          </div>
          <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter leading-[0.95] mb-6">
            Run elections<br />people actually<br /><span className="text-[#1F6FE5]">trust.</span>
          </h1>
          <p className="text-lg text-zinc-600 leading-relaxed max-w-xl mb-10">
            SelectFlow gives organisations secure, anonymous, verifiable voting — from candidate ballots to ranked-choice — with a voting page built for the phone in every voter's pocket.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link data-testid="hero-cta" to="/auth?mode=register" className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#1F6FE5] text-white rounded-full font-semibold hover:bg-[#195AB8] transition-colors active:scale-95 group">
              Create your organisation <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link data-testid="hero-signin" to="/auth" className="inline-flex items-center px-6 py-3.5 border border-zinc-300 rounded-full font-semibold hover:border-black transition-colors">Sign in</Link>
            <DemoButton />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-28 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="bg-white border border-zinc-200 p-7 hover:-translate-y-1 hover:shadow-md transition-all" style={{ animationDelay: `${i * 80}ms` }}>
              <f.icon size={30} weight="duotone" className="text-[#1F6FE5] mb-5" />
              <h3 className="font-display font-bold text-xl tracking-tight mb-2">{f.title}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
